/**
 * One-screen home inventory item.
 * system_type ids stay the ones task matching already uses
 * (pool_equipment, hvac, septic_system, and the rest of SYSTEM_CATEGORIES).
 * Extra keys already stored in system_data are kept.
 */

import { featureSelected, featuresFromHomeAnswers } from './homeownerIntake';

const INVENTORY_TYPES = [
  { id: 'electrical_panel', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'water_filter', label: 'Water filter' },
  { id: 'pool_equipment', label: 'Pool' },
  { id: 'solar_panels', label: 'Solar' },
  { id: 'washer_dryer', label: 'Washer & dryer' },
  { id: 'refrigerator', label: 'Fridge' },
  { id: 'dishwasher', label: 'Dishwasher' },
  { id: 'oven_range', label: 'Oven' },
  { id: 'microwave', label: 'Microwave' },
];

const OPTIONAL_HOME_TYPES = [
  { id: 'solar_panels', label: 'Solar', feature: 'solar_panels' },
  { id: 'pool_equipment', label: 'Pool', feature: 'pool' },
];

const OPTIONAL_TYPE_IDS = new Set(OPTIONAL_HOME_TYPES.map((type) => type.id));

function inventoryTypesForHome(features, includeTypeId) {
  const selected = new Set(
    OPTIONAL_HOME_TYPES
      .filter((type) => featureSelected(features, type.feature))
      .map((type) => type.id)
  );
  if (includeTypeId) selected.add(includeTypeId);
  return INVENTORY_TYPES.filter((type) => !OPTIONAL_TYPE_IDS.has(type.id) || selected.has(type.id));
}

const DETAIL_FIELDS = [
  { key: 'model_number', label: 'Model' },
  { key: 'serial_number', label: 'Serial' },
  { key: 'warranty_years', label: 'Warranty' },
  { key: 'notes', label: 'Notes' },
  { key: 'specs', label: 'Specs' },
];

const YEAR_KEYS = ['install_year', 'purchase_year', 'pump_year', 'washer_year', 'heater_year'];

const RESERVED_KEYS = new Set([
  'name', 'manufacturer', 'brand',
  'install_year', 'purchase_year', 'pump_year', 'washer_year', 'heater_year',
  'filter_year', 'dryer_year',
  'model_number', 'serial_number', 'warranty_years', 'notes', 'specs',
  'photo_path', 'photo_name', 'photo_document_id', 'tracked',
  'washer_manufacturer', 'dryer_manufacturer',
]);

const HIDDEN_DISPLAY_KEYS = new Set([
  'tracked', 'photo_path', 'photo_name', 'photo_document_id',
]);

function clean(value) {
  if (value == null) return '';
  return String(value).trim();
}

function labelForKey(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function readBrand(data) {
  const row = data || {};
  return clean(row.name) || clean(row.manufacturer) || clean(row.brand) || clean(row.washer_manufacturer);
}

function primaryYearKey(data) {
  const row = data || {};
  const found = YEAR_KEYS.find((key) => clean(row[key]));
  return found || 'install_year';
}

function readInstallYear(data) {
  const key = primaryYearKey(data);
  return clean((data || {})[key]);
}

function yearFromAge(age, nowYear) {
  const years = parseInt(age, 10);
  const current = Number(nowYear);
  if (!Number.isFinite(years) || years < 0 || years > 150) return '';
  if (!Number.isFinite(current)) return '';
  return String(current - years);
}

function ageFromYear(year, nowYear) {
  const installed = parseInt(year, 10);
  const current = Number(nowYear);
  if (!Number.isFinite(installed) || !Number.isFinite(current)) return '';
  const age = current - installed;
  if (age < 0 || age > 150) return '';
  return String(age);
}

function readDetails(data) {
  const row = data || {};
  const details = {};
  DETAIL_FIELDS.forEach((field) => {
    details[field.key] = clean(row[field.key]);
  });
  return details;
}

function extraFields(data) {
  const row = data || {};
  return Object.keys(row)
    .filter((key) => !RESERVED_KEYS.has(key) && clean(row[key]) && clean(row[key]).toLowerCase() !== 'not visible')
    .map((key) => ({ key, label: labelForKey(key), value: clean(row[key]) }));
}

function emptyForm() {
  return {
    systemType: '',
    brand: '',
    installYear: '',
    age: '',
    details: readDetails({}),
    extras: {},
  };
}

function formFromSystem(systemType, data, nowYear) {
  const year = readInstallYear(data);
  const extras = {};
  extraFields(data).forEach((field) => {
    extras[field.key] = field.value;
  });
  return {
    systemType: systemType || '',
    brand: readBrand(data),
    installYear: year,
    age: ageFromYear(year, nowYear),
    details: readDetails(data),
    extras,
  };
}

function buildSystemData(existing, input, nowYear) {
  const prior = existing && typeof existing === 'object' ? existing : {};
  const next = { ...prior };
  const brand = clean(input && input.brand);
  const typedYear = clean(input && input.installYear);
  const year = typedYear || yearFromAge(input && input.age, nowYear);
  const yearKey = primaryYearKey(prior);

  if (brand) {
    delete next.name;
    delete next.brand;
    next.manufacturer = brand;
    if (Object.prototype.hasOwnProperty.call(prior, 'washer_manufacturer')) next.washer_manufacturer = brand;
    if (Object.prototype.hasOwnProperty.call(prior, 'dryer_manufacturer')) next.dryer_manufacturer = brand;
  } else {
    delete next.name;
    delete next.manufacturer;
    delete next.brand;
  }

  if (year) {
    next.install_year = year;
    if (yearKey !== 'install_year') next[yearKey] = year;
  } else {
    delete next.install_year;
    if (yearKey !== 'install_year') delete next[yearKey];
  }

  const details = (input && input.details) || {};
  DETAIL_FIELDS.forEach((field) => {
    const value = clean(details[field.key]);
    if (value) next[field.key] = value;
    else delete next[field.key];
  });

  const extras = (input && input.extras) || {};
  Object.keys(extras).forEach((key) => {
    if (RESERVED_KEYS.has(key)) return;
    const value = clean(extras[key]);
    if (value) next[key] = value;
    else delete next[key];
  });

  const photo = input && input.photo;
  if (photo && photo.path) {
    next.photo_path = photo.path;
    if (photo.name) next.photo_name = photo.name;
    if (photo.documentId) next.photo_document_id = photo.documentId;
  }

  delete next.tracked;
  const hasValue = Object.keys(next).some((key) => clean(next[key]));
  if (!hasValue) next.tracked = true;
  return next;
}

function inventoryFactLines(data) {
  const row = data || {};
  const lines = [];
  const brand = readBrand(row);
  if (brand) lines.push({ label: 'Brand', value: brand });
  const year = readInstallYear(row);
  if (year) lines.push({ label: 'Year', value: year });
  DETAIL_FIELDS.forEach((field) => {
    const value = clean(row[field.key]);
    if (value) lines.push({ label: field.label, value });
  });
  if (clean(row.material)) lines.push({ label: 'Material', value: clean(row.material) });
  if (clean(row.type)) lines.push({ label: 'Type', value: clean(row.type) });
  extraFields(row).forEach((field) => {
    if (field.key === 'material' || field.key === 'type') return;
    lines.push({ label: field.label, value: field.value });
  });
  return lines;
}

function visibleSystemEntries(data) {
  return Object.entries(data || {}).filter(([key, value]) => {
    if (HIDDEN_DISPLAY_KEYS.has(key)) return false;
    return clean(value) && clean(value).toLowerCase() !== 'not visible';
  });
}

function systemsMapFromRows(rows, systemType, systemData) {
  const systems = {};
  (rows || []).forEach((row) => {
    if (row && row.system_type) systems[row.system_type] = row.system_data || {};
  });
  if (systemType) systems[systemType] = systemData || {};
  return systems;
}

function featuresAfterInventorySave({ features, systems }) {
  return featuresFromHomeAnswers({ features, systems });
}

export {
  INVENTORY_TYPES,
  OPTIONAL_HOME_TYPES,
  inventoryTypesForHome,
  DETAIL_FIELDS,
  HIDDEN_DISPLAY_KEYS,
  readBrand,
  readDetails,
  readInstallYear,
  yearFromAge,
  ageFromYear,
  extraFields,
  emptyForm,
  formFromSystem,
  buildSystemData,
  inventoryFactLines,
  visibleSystemEntries,
  systemsMapFromRows,
  featuresAfterInventorySave,
};
